import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";

import moment from "moment";

import TablePagination from "../../../components/TablePagination";
import { listarRomaneios } from "../../../services/romaneioManualApi";
import { FIM_EXECUCAO, associarNotas, consultarExecucaoRecalculo, listarNotasCandidatas } from "../../../services/nfseApi";
import {
  Aviso,
  CheckboxTodos,
  EstadoTabela,
  DATE_FORMAT,
  ModalHub,
  SelecaoResumo,
  extractError,
  fieldInputStyle,
  fmtBRL,
  fmtData,
  fmtNumber,
  fmtPeso,
  periodoMesAtual,
  useRemetentes,
  useSelecao,
} from "../_documentosFrete/comum";

const PAGE_SIZES = [20, 50, 100];
const INTERVALO_POLL_MS = 2500;

// Associa notas a uma NFse (PO1-3301). Candidatas por um romaneio da
// transportadora (tela Romaneio, em Despacho; qualquer romaneio serve de filtro) OU por
// remetente × período. O POST devolve o executionId do
// recálculo; o modal acompanha a execução (mesma consulta da Auditoria de
// Faturas) e só libera "Fechar" com o resultado.
//
// O embarcador vem da própria NFse (idEmbarcador): com "Todos os embarcadores"
// selecionado o empresaId é null, e o recálculo e os remetentes precisam de um.
// Período padrão dos romaneios: mês da emissão da NFse ± 1 mês (editável).
export function periodoRomaneios(datEmissao) {
  const dia = datEmissao ? moment(String(datEmissao).slice(0, 10), DATE_FORMAT) : null;
  const base = dia && dia.isValid() ? dia : moment();
  return {
    de: base.clone().startOf("month").subtract(1, "month").format(DATE_FORMAT),
    ate: base.clone().add(1, "month").endOf("month").format(DATE_FORMAT),
  };
}

export default function AssociarNotasModal({ nfse, empresaId, podeEditar = true, onClose }) {
  const idEmbarcador = nfse.idEmbarcador || empresaId || null;
  const remetentes = useRemetentes(idEmbarcador);
  const [romaneios, setRomaneios] = useState([]);
  const [erroRomaneios, setErroRomaneios] = useState("");
  const [periodoRom, setPeriodoRom] = useState(() => periodoRomaneios(nfse.datEmissao));
  const [modo, setModo] = useState("romaneio"); // romaneio | periodo
  const [idRomaneio, setIdRomaneio] = useState("");
  const [idRemetente, setIdRemetente] = useState(nfse.idEmbarcadorRemetente ? String(nfse.idEmbarcadorRemetente) : "");
  const [periodo, setPeriodo] = useState(periodoMesAtual);

  const [candidatas, setCandidatas] = useState({ total: 0, itens: [] });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [erroAssociar, setErroAssociar] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const selecao = useSelecao();

  const [associando, setAssociando] = useState(false);
  const associandoRef = useRef(false); // trava o clique duplo antes do re-render
  const [execucao, setExecucao] = useState(null); // { executionId, vinculadas, texto, fim, erros }

  // Romaneios da transportadora da NFse com embarque no período (a lista do
  // tela Romaneio; os que já têm documento aparecem marcados, não somem).
  useEffect(() => {
    let vivo = true;
    setErroRomaneios("");
    if (!periodoRom.de || !periodoRom.ate) {
      setRomaneios([]);
      return undefined;
    }
    const params = { idTransportadora: nfse.idTransportadora, de: periodoRom.de, ate: periodoRom.ate, pageSize: 200 };
    if (idEmbarcador) params.idEmbarcador = idEmbarcador;
    listarRomaneios(params)
      .then((r) => vivo && setRomaneios((r && r.itens) || []))
      .catch((e) => {
        if (!vivo) return;
        setRomaneios([]);
        setErroRomaneios(extractError(e, "Não foi possível listar os romaneios."));
      });
    return () => {
      vivo = false;
    };
  }, [idEmbarcador, nfse.idTransportadora, periodoRom.de, periodoRom.ate]);

  const filtroPronto = modo === "romaneio" ? Boolean(idRomaneio) : Boolean(idRemetente && periodo.de && periodo.ate);

  const carregar = useCallback(async () => {
    if (!filtroPronto) {
      setCandidatas({ total: 0, itens: [] });
      return;
    }
    setLoading(true);
    setErro("");
    try {
      const params = { page, pageSize };
      if (modo === "romaneio") params.idRomaneio = Number(idRomaneio);
      else {
        params.idEmbarcadorRemetente = Number(idRemetente);
        params.de = periodo.de;
        params.ate = periodo.ate;
      }
      const r = await listarNotasCandidatas(nfse.id, params);
      setCandidatas({ total: Number(r.total) || 0, itens: r.itens || [] });
    } catch (e) {
      setCandidatas({ total: 0, itens: [] });
      setErro(extractError(e, "Não foi possível listar as notas candidatas."));
    } finally {
      setLoading(false);
    }
  }, [filtroPronto, modo, idRomaneio, idRemetente, periodo.de, periodo.ate, page, pageSize, nfse.id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Trocar o filtro limpa a seleção (as candidatas são outras).
  useEffect(() => {
    setPage(1);
    selecao.limpar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo, idRomaneio, idRemetente, periodo.de, periodo.ate]);

  const associar = async () => {
    if (associandoRef.current || execucao || !podeEditar || !selecao.ids.length) return;
    associandoRef.current = true;
    setAssociando(true);
    setErroAssociar("");
    try {
      const r = await associarNotas(nfse.id, selecao.ids);
      const vinculadas = Number(r && r.vinculadas) || selecao.ids.length;
      if (r && r.executionId) {
        setExecucao({ executionId: r.executionId, idEmbarcador: r.idEmbarcador || idEmbarcador, vinculadas, fim: false, texto: `${fmtNumber(vinculadas)} nota(s) associada(s). Recalculando o frete…` });
      } else {
        setExecucao({ executionId: null, vinculadas, fim: true, texto: `${fmtNumber(vinculadas)} nota(s) associada(s) à NFse ${nfse.numero}.` });
      }
    } catch (e) {
      setErroAssociar(extractError(e, "Não foi possível associar as notas."));
    } finally {
      associandoRef.current = false;
      setAssociando(false);
    }
  };

  // Acompanha a execução do recálculo até terminar.
  const executionId = execucao && !execucao.fim ? execucao.executionId : null;
  const embarcadorExecucao = execucao ? execucao.idEmbarcador : null;
  useEffect(() => {
    if (!executionId) return undefined;
    let ativo = true;
    let timer = null;
    const consultar = async () => {
      try {
        const ex = await consultarExecucaoRecalculo(executionId, embarcadorExecucao ? { embarcadorId: embarcadorExecucao } : {});
        if (!ativo) return;
        const total = Number(ex.total) || 0;
        const processados = Number(ex.processed) || 0;
        if (!FIM_EXECUCAO.includes(ex.status)) {
          setExecucao((atual) => (atual ? { ...atual, texto: `Recalculando: ${fmtNumber(processados)} de ${fmtNumber(total)}…` } : atual));
          timer = setTimeout(consultar, INTERVALO_POLL_MS);
          return;
        }
        const contagem = ex.counts || {};
        const erros = (Number(contagem.businessErrors) || 0) + (Number(contagem.technicalErrors) || 0);
        setExecucao((atual) => ({
          ...atual,
          fim: true,
          erros,
          texto: erros
            ? `Recálculo concluído: ${fmtNumber(total - erros)} de ${fmtNumber(total)} recalculado(s); ${fmtNumber(erros)} não calcularam (bloqueados ou sem tabela).`
            : `Recálculo concluído: ${fmtNumber(total)} documento(s) recalculado(s).`,
        }));
      } catch (e) {
        if (!ativo) return;
        setExecucao((atual) => ({ ...atual, fim: true, erros: 1, texto: extractError(e, "Não foi possível acompanhar o recálculo.") }));
      }
    };
    consultar();
    return () => {
      ativo = false;
      if (timer) clearTimeout(timer);
    };
  }, [executionId, embarcadorExecucao]);

  const fechar = () => {
    if (execucao && !execucao.fim) return; // espera o recálculo terminar
    onClose(Boolean(execucao));
  };
  const podeAssociar = podeEditar && !associando && !execucao && selecao.ids.length > 0;

  return (
    <ModalHub titulo={`Associar notas à NFse ${nfse.numero}`} icone="tim-icons icon-link-72" onClose={fechar} maxWidth={900}>
      <div className="docs-frete__caixa">
        <div>
          <strong>{nfse.transportadora || `Transportadora #${nfse.idTransportadora}`}</strong> · emissão {fmtData(nfse.datEmissao)} ·
          valor <strong>{fmtBRL(nfse.valor)}</strong>
        </div>
        <div style={{ color: "var(--everlog-card-muted)", fontSize: "0.8rem" }}>
          Tomador {nfse.embarcadorRemetente || (nfse.idEmbarcadorRemetente ? `#${nfse.idEmbarcadorRemetente}` : "-")} · série{" "}
          {nfse.serie || "-"} · chave <span className="docs-frete__chave">{nfse.chave || "-"}</span>
        </div>
      </div>

      {!execucao ? (
        <div className="docs-frete__form">
          <div className="docs-frete__abas" role="tablist" style={{ marginBottom: "0.75rem" }}>
            <button type="button" role="tab" aria-selected={modo === "romaneio"} className={`docs-frete__aba${modo === "romaneio" ? " is-active" : ""}`} onClick={() => setModo("romaneio")}>
              Por romaneio
            </button>
            <button type="button" role="tab" aria-selected={modo === "periodo"} className={`docs-frete__aba${modo === "periodo" ? " is-active" : ""}`} onClick={() => setModo("periodo")}>
              Por remetente e período
            </button>
          </div>
          {modo === "romaneio" ? (
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="nfse-romaneio">Romaneio</Label>
                  <Input id="nfse-romaneio" type="select" value={idRomaneio} onChange={(e) => setIdRomaneio(e.target.value)} style={fieldInputStyle}>
                    <option value="">Selecione o romaneio...</option>
                    {romaneios.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.codigo || `#${r.id}`} · {fmtData(r.dataSaida)} · {fmtNumber(r.qtdNotas)} notas{r.temDocumento ? " · com documento" : ""}
                      </option>
                    ))}
                  </Input>
                  {erroRomaneios ? (
                    <small style={{ color: "var(--everlog-status-danger)" }}>{erroRomaneios}</small>
                  ) : !romaneios.length ? (
                    <small style={{ color: "var(--everlog-card-muted)" }}>Nenhum romaneio desta transportadora com embarque no período.</small>
                  ) : null}
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label for="nfse-romaneio-de">Embarque de</Label>
                  <Input id="nfse-romaneio-de" type="date" value={periodoRom.de} onChange={(e) => setPeriodoRom((p) => ({ ...p, de: e.target.value }))} style={fieldInputStyle} />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label for="nfse-romaneio-ate">Até</Label>
                  <Input id="nfse-romaneio-ate" type="date" value={periodoRom.ate} onChange={(e) => setPeriodoRom((p) => ({ ...p, ate: e.target.value }))} style={fieldInputStyle} />
                </FormGroup>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label for="nfse-remetente">Remetente</Label>
                  <Input id="nfse-remetente" type="select" value={idRemetente} onChange={(e) => setIdRemetente(e.target.value)} style={fieldInputStyle}>
                    <option value="">Selecione o remetente...</option>
                    {remetentes.map((r) => (
                      <option key={r.Id} value={r.Id}>
                        {r.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label for="nfse-de">De</Label>
                  <Input id="nfse-de" type="date" value={periodo.de} onChange={(e) => setPeriodo((p) => ({ ...p, de: e.target.value }))} style={fieldInputStyle} />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label for="nfse-ate">Até</Label>
                  <Input id="nfse-ate" type="date" value={periodo.ate} onChange={(e) => setPeriodo((p) => ({ ...p, ate: e.target.value }))} style={fieldInputStyle} />
                </FormGroup>
              </Col>
            </Row>
          )}

          <p className="docs-frete__secao">Notas candidatas · {fmtNumber(candidatas.total)}</p>
          {selecao.totais.qtd > 0 ? (
            <SelecaoResumo totais={selecao.totais}>
              <Button color="default" size="sm" outline onClick={selecao.limpar}>
                Desmarcar seleção
              </Button>
            </SelecaoResumo>
          ) : null}
          <EstadoTabela
            loading={loading}
            erro={erro}
            vazio={!loading && !candidatas.itens.length}
            textoCarregando="Consultando notas candidatas..."
            textoVazio={filtroPronto ? "Nenhuma nota candidata para este filtro." : "Escolha o romaneio ou o remetente e o período."}
          />
          {!loading && !erro && candidatas.itens.length ? (
            <>
              <Table className="docs-frete__tabela hub-thead-accent" size="sm" responsive hover>
                <thead className="text-primary">
                  <tr>
                    <th className="is-sel">
                      <CheckboxTodos itens={candidatas.itens} selecao={selecao} />
                    </th>
                    <th>Nota</th>
                    <th>Emissão</th>
                    <th>Romaneio</th>
                    <th className="is-number">Peso</th>
                    <th className="is-number">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {candidatas.itens.map((n) => (
                    <tr key={n.id}>
                      <td className="is-sel">
                        <input type="checkbox" aria-label={`Selecionar nota ${n.numero}`} checked={selecao.has(n.id)} onChange={() => selecao.toggle(n)} />
                      </td>
                      <td>
                        <strong>{n.numero}</strong>
                        <small>Série {n.serie || "-"}</small>
                      </td>
                      <td className="docs-frete__nowrap">{fmtData(n.datEmissao)}</td>
                      <td>{n.romaneioCodigo || (n.idRomaneio ? `#${n.idRomaneio}` : "-")}</td>
                      <td className="is-number">{fmtPeso(n.pesoBruto)}</td>
                      <td className="is-number">{fmtBRL(n.valorTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <TablePagination
                page={page}
                pageSize={pageSize}
                total={candidatas.total}
                pageSizes={PAGE_SIZES}
                itemLabel="nota(s)"
                onPageChange={setPage}
                onPageSizeChange={(novo) => {
                  setPageSize(novo);
                  setPage(1);
                }}
              />
            </>
          ) : null}
        </div>
      ) : (
        <Aviso tom={execucao.fim ? (execucao.erros ? "aviso" : "ok") : "info"}>
          {!execucao.fim ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
          {execucao.texto}
        </Aviso>
      )}

      {!podeEditar && !execucao ? (
        <Aviso tom="info">Você pode consultar as notas candidatas, mas não tem permissão para associá-las à NFse.</Aviso>
      ) : null}
      {erroAssociar ? <Aviso tom="erro">{erroAssociar}</Aviso> : null}

      <div className="docs-frete__modal-rodape">
        <Button color="secondary" size="sm" onClick={fechar} disabled={associando || (execucao && !execucao.fim)}>
          {execucao ? "Fechar" : "Cancelar"}
        </Button>
        {!execucao && podeEditar ? (
          <Button color="primary" size="sm" onClick={associar} disabled={!podeAssociar}>
            {associando ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
            {associando ? "Associando..." : "Associar"}
          </Button>
        ) : null}
      </div>
    </ModalHub>
  );
}
